import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  useIsFetching,
  useIsMutating,
  useQueryClient,
} from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useNavigate, useSearchParams } from "react-router"
import {
  openQuestionBatches,
  useDeclarationInquiries,
  type DeclarationInquiry,
} from "../../../../../api/declarationInquiriesApi"
import { useDeclarationQuestions } from "../../../../../api/declarationQuestionsApi"
import {
  useApproveDeclaration,
  useReviewNow,
} from "../../../../../api/declarationsApi"
import { isReviewNowMutation } from "../../../../../api/mutationKeys"
import { announceRecompute } from "../../../../../api/recomputeSignal"
import { useRecomputeStatus } from "../../../../../api/recomputeStatusApi"
import type { ApproveResult } from "../../../../../api/types"
import { DECLARATIONS_BASE } from "../../../routes"
import type { WorklistIssue } from "../../studio/cellState"
import {
  openItemsUnsettled,
  type OpenItemsStatus,
} from "../../studio/serverWorklist"
import type { Verdict } from "../../studio/studioTypes"
import { useRunPoll } from "../../studio/useRunPoll"
import type { PrimaryAction } from "../mapping/deriveFrameworkVerdict"
import {
  declarationGeprueft,
  openWorkByFramework,
} from "../mapping/frameworkGeprueft"
import { openPointDetailText } from "../mapping/openPointDetail"
import { nextDeclarationSearch } from "../studioV2Url"
import type { StatementCard } from "../useStudioV2State"
import { ApproveConfirmDialog } from "./ApproveConfirmDialog"
import { ApproveGatePanel } from "./ApproveGatePanel"
import {
  approveGateDetailOf,
  type ApproveGateDetail,
  type ApproveOpenQuestionBatch,
} from "./approveGateTypes"
import { BusyLabel } from "./BusyLabel"
import { issueReasonKey } from "./issueReason"
import { issuesInDisplayOrder } from "./nextOpenPoint"
import {
  countOpenPoints,
  defaultCardOrderOf,
  defaultLabelOf,
  inFrameworkOrder,
  OpenPointsPopover,
} from "./OpenPointsPopover"
import {
  OpenQuestionsAckDialog,
  type OpenQuestionBatchView,
} from "./OpenQuestionsAckDialog"
import { RecomputeStatusLine } from "./RecomputeStatusLine"
import { buildRequestSearchParams, type RequestContext } from "./requestContext"
import { RequestQuestionsDialog } from "./RequestQuestionsDialog"

/** Normalises a client-known open question batch (`openQuestionBatches` over
 *  `useDeclarationInquiries`) into what `OpenQuestionsAckDialog` reads. */
function localBatchView(row: DeclarationInquiry): OpenQuestionBatchView {
  return {
    inquiryId: row.inquiryId,
    status: row.status,
    sentAt: row.sentAt,
    questionCount: row.questionCount,
  }
}

/** Same normalisation for the server's own late-arriving 409 detail (contract
 *  2), snake_case, since `approveGateDetailOf` reads the error body raw. A
 *  batch sent after this page loaded (or the late re-check) never reached the
 *  client read above, so this is the ONLY source for it. */
function serverBatchView(b: ApproveOpenQuestionBatch): OpenQuestionBatchView {
  return {
    inquiryId: b.inquiry_id,
    status: b.status,
    sentAt: b.sent_at,
    questionCount: b.question_count,
  }
}

/** Studio V2's own inquiry-state reading — narrower than
 *  `DeclarationInquiryState` (never "draft", the composer-only local state).
 *  Defined here rather than imported from `declarationInquiriesApi.ts` so
 *  this file compiles standalone in B10, before that wiring lands in D2. */
export type FooterInquiryState = "none" | "sent" | "answered"

export interface StudioV2FooterBarProps {
  declId: string
  frameworks: string[]
  worklist: WorklistIssue[]
  /** Paged mode (C1-d): where the server's open items stand. "pending" (no
   *  answer yet) and "unknown" (the read failed) keep Freigeben blocked and say
   *  so on the gate line: the other regulations' share of `worklist` is not
   *  settled. "refreshing" (a newer answer on its way after a gesture) only
   *  disables it, like `saving`, so the primary look does not flicker on every
   *  save. Absent/null/"fresh": the ordinary bar, byte-identical to before. */
  openItemsStatus?: OpenItemsStatus | null
  primaryAction: PrimaryAction
  nameOf: (framework: string) => string
  /** The next `lifecycleStatus === "ready"` declaration, or null when this is
   *  the last one — "Überspringen" and Übernehmen's onDone both read it. */
  nextDeclarationId: string | null
  onApproved: (result: ApproveResult) => void
  /** The 409 ALREADY_APPROVED race — someone else approved this declaration
   *  between this reviewer's last read and this click. Optional so every
   *  existing render/test that never passes it keeps behaving exactly as
   *  before (toast + refetch only, review mask stays mounted). When given,
   *  called AFTER the toast+refetch: `StudioV2Layout` wires it to the SAME
   *  "move on" path a genuine local approval finishes on
   *  (`goToNextOrDone` — next `ready` declaration, else the done screen's
   *  "completed" reason), the honest fit given there is no `ApproveResult`
   *  payload here to hand `ApprovedView`: from this reviewer's own
   *  perspective there is no more open work left on this declaration either
   *  way, which is exactly what that path already says. */
  onAlreadyApproved?: () => void
  /** Per-framework Rückfrage state. Defaults to "always none" — the D2 PR
   *  wires the real `useDeclarationInquiries` read through
   *  `useStudioV2State`; until then Übernehmen's inquiry-gate simply never
   *  fires, which is the correct behaviour for a declaration with no
   *  Rückfrage feature at all. */
  inquiryStateOf?: (framework: string) => FooterInquiryState
  /** Articles each framework's open Rückfrage names. A sent Rückfrage WITH
   *  articles no longer gates Übernehmen: approve holds those articles back and
   *  the gate line says how many wait for the reply. Defaults to 0, so a sent
   *  Rückfrage keeps gating for every caller that does not pass it. */
  inquiryArticleCountOf?: (framework: string) => number
  /** Whether this framework's open Rückfrage went to a dead address (bounced or
   *  flagged as spam). Read SEPARATELY from the article count: the server
   *  reports `holdsBack` false for such a row, so its count arrives as 0 even
   *  when it names articles, and the gate would otherwise explain the block
   *  with the no-articles reason. Defaults to "never dead", which is what every
   *  caller predating this prop already assumed. */
  inquiryIsDeadAddressOf?: (framework: string) => boolean
  /** Überspringen with no next declaration to jump to — B11's done screen.
   *  Undefined falls back to a plain navigate to the declarations list
   *  (B10's original behaviour, and what a standalone render/test gets). */
  onNoNextDeclaration?: () => void
  /** MC_DECL_V2_SUP — `useStudioV2State`'s own supplier-match read off the
   *  extraction query. `null` (the default) means "not yet known" and never
   *  blocks Anfordern — only a confirmed `false` does, since the server 422s
   *  NO_SUPPLIER on that path anyway. */
  supplierMatched?: boolean | null
  /** The bound master-data supplier's id, once matched — threads into
   *  Anfordern's `?supplierIds=` deep link so the inquiry composer opens
   *  with this declaration's supplier pre-checked instead of landing on an
   *  empty picker. Undefined/null (unmatched, or an older read model) just
   *  omits the param, same as today. */
  supplierId?: string | null
  /** The document's own verbatim issuer name — threaded to the approve
   *  gate's NO_SUPPLIER branch so its create-form prefill matches the
   *  breadcrumb chip's. */
  supplierMentioned?: string | null
  /** MC_SUPPLIER_REASSIGN: the approve gate's SUPPLIER_CHANGED way out, the
   *  re-extraction against the bound supplier. Absent: that branch shows its
   *  copy without the button. */
  onReextract?: () => void
  /** Real `mc_articles.id`s (NOT article numbers) still open on the
   *  framework the reviewer is currently looking at — `StudioV2Layout`
   *  resolves the worklist's article numbers against `state.pool` before
   *  passing these down, since the inquiry-create endpoint matches on id.
   *  Threads into Anfordern's `?articleIds=` so the created inquiry scopes
   *  to exactly what's still outstanding instead of the composer's full-
   *  catalogue default. Empty/undefined omits the param. */
  openArticleIds?: string[]
  /** STEP C3-b2 (paged review): the pool with ids may not be loaded yet when the
   *  reviewer presses Anfordern, so the ids are resolved at click time instead
   *  (`/scope` fetched on demand). Absent: `openArticleIds`, as today. */
  resolveOpenArticleIds?: () => Promise<string[]>
  /** The review context Anfordern carries into the composer beyond supplier
   *  and articles (TUR-1211, Erik 02.09 bug 3): active Verordnung, the
   *  declaration/statement under review and the "Bezug" line for the mail
   *  draft. See `requestContext.ts`. Optional; absent ⇒ #2120's link as is. */
  requestContext?: RequestContext | null
  /** Switches the mask to a statement named by an approve-gate refusal or
   *  the open-points map: `step` (W5-4) also opens that step's own dialog
   *  on the landed card, straight through from `ApproveGatePanel`/
   *  `OpenPointsPopover`. */
  onGoToStatement?: (
    framework: string,
    statementId: string,
    step?: 1 | 2 | 3,
  ) => void
  /** Switches the mask to a Verordnung — D1's map uses it for blockers that
   *  have no statement to land on (the reference-list conflict, an open
   *  Rückfrage). Optional like `onGoToStatement`, for the same reason. */
  onGoToFramework?: (framework: string) => void
  /** Threaded straight to `OpenPointsPopover` — see its own doc. Optional so
   *  every existing render/test that predates TUR-1211's card reorder keeps
   *  the old `groupIndex + 1` label. */
  labelOf?: (
    framework: string,
    statementId: string,
    groupIndex: number | undefined,
  ) => number
  /** Threaded straight to `OpenPointsPopover` — see its own doc. */
  cardOrderOf?: (framework: string) => readonly string[]
  /** Left slot of the action bar — the declaration-wide "alle konformen als
   *  geprüft markieren" bulk gesture (`BulkConformGesture`). Promoted here
   *  from the navigator's own footer, where it rendered as an ellipsized
   *  micro-label while this bar's left half sat empty. Optional: a
   *  single-Verordnung declaration has nothing to bulk over. */
  bulkAction?: ReactNode
  /** The guided layer's "Weiter" jump — standard behavior (TUR-1211).
   *  `StudioV2Layout` always passes it; kept optional here so this bar
   *  still renders without the button when a caller omits it. */
  onNextOpenPoint?: () => void
  /** Every statement card across every Verordnung (TUR-1211), passed
   *  straight through to `RequestQuestionsDialog`'s own `cards` prop, see
   *  its doc for why the dialog needs it handed in rather than reading it
   *  itself, and read for each statement's verdict (`verdictOf`, TUR-1676).
   *  Optional, defaulting to none: every caller/test predating this prop
   *  keeps the dialog's plain fallback-body line format and the step-3
   *  substances wording. */
  cards?: StatementCard[]
  /** STEP C3-b3, paged review: `cards` holds the open regulation plus whichever
   *  others were loaded. This loads other regulations' cards: all of them before the
   *  request dialog opens (its fallback body quotes each statement), the ones with
   *  open work when the open-points map opens (each row's reason). Resolves to the
   *  regulations that could not be loaded. */
  loadAllCards?: (options?: { openOnly?: boolean }) => Promise<string[] | void>
  /** STEP C3-b3, paged review: a statement's verdict from the server's summary. It
   *  wins over a kept card of another regulation (that copy can predate a write);
   *  the caller answers nothing for the open regulation, whose cards are live. */
  summaryVerdictOf?: (statementId: string) => Verdict | undefined
  /** Focus mode's compact bar (design spec "Flow"), one object rather than
   *  four independent optional props: the three fields only make sense
   *  together, so `compact` is derived here as `focusFooter !== undefined`
   *  rather than settable on its own with a handler missing or a stale
   *  default. Present: hides the whole-declaration Skip and, while
   *  `activeFrameworkGeprueft` is false, swaps Approve for the
   *  per-Verordnung "Als geprüft und weiter" gesture plus its own
   *  framework-only Überspringen. Absent (every pre-existing caller/test)
   *  keeps rendering byte-identical. */
  focusFooter?: {
    /** The SAME bundle-conform-then-advance gesture Enter fires
     *  (`StudioV2Layout`'s `handleConformActiveAndAdvance`), compact's
     *  primary before the active Verordnung is geprueft. */
    onConformActive: () => void
    /** Compact's secondary: advances past the active Verordnung WITHOUT
     *  conforming it (`advancePastActive(active, false)`). */
    onSkipFramework: () => void
    /** Whether the ACTIVE Verordnung (not the whole declaration) is
     *  geprueft, gating which compact primary renders. */
    activeFrameworkGeprueft: boolean
  }
}

const ACTION_ORDER: PrimaryAction[] = ["accept", "request", "defer"]

// Module-level, not `= []` in the destructuring default: a literal there
// allocates a NEW array every render a caller omits the prop, which would
// defeat `useImperativeHandle`'s dep-array memoization below (a fresh
// reference every render reads as "changed" even when nothing did).
const NO_ARTICLE_IDS: string[] = []
const NO_CARDS: StatementCard[] = []

/**
 * THE 409 `REVIEW_PENDING` WAIT — how long to sit between automatic retries,
 * and how many idle rounds that wait is worth.
 *
 * `approve` refuses while the run's deferred final review has not seen the
 * declaration as it now stands, AND STARTS THAT REVIEW ON ITS WAY OUT
 * (`review_started`). Before this, that left the reviewer to notice the wait
 * had ended and press Übernehmen a second time (product-owner feedback,
 * 03.09.) — a second press that carries no new decision, only the timing the
 * screen already knows better than they do. So the refusal now ARMS THE RUN
 * FEED (`useRunPoll`) and retries on its own.
 *
 * The run feed is what makes the delay honest rather than guessed: while a
 * cone is walking, `inFlight` is true and no retry is spent at all — the
 * budget below only counts IDLE rounds, i.e. time when the feed says nothing
 * is happening (the cone was missed, or the review settled between two
 * polls). Twelve of those at five seconds is a minute of quiet waiting, well
 * past a review that is actually running, and short of a wait long enough to
 * read as a hung screen. Exhausting it leaves the gate panel standing with
 * its manual copy — never a dead end, and never an unbounded poll.
 */
const REVIEW_RETRY_MS = 5_000
const REVIEW_RETRY_MAX_IDLE_ROUNDS = 12
/** THE WAIT'S OUTER BOUND, wall-clock and answerable to nothing else.
 *
 *  The idle-round budget above is reset by `saving` and by `run.inFlight`,
 *  which is right for what it measures (silence) and is exactly why it can
 *  never arrive on its own: on 17.09 a sandbox declaration sat in this wait
 *  for sixteen minutes, re-pressing approve every five seconds, because the
 *  run feed kept reporting a cone in flight while the review it was waiting
 *  for was queued behind other work and never started. A second bound that
 *  no signal can push out ends that: three minutes is well past any review
 *  that is genuinely about to settle, and what it hands back is the press
 *  itself, never a dead end.
 *
 *  Wall-clock rather than a round count on purpose: a backgrounded tab
 *  throttles `setTimeout` to roughly once a minute, so counting rounds would
 *  stretch the same three minutes into the better part of an hour. */
const REVIEW_RETRY_MAX_TOTAL_MS = 180_000
/** TUR-1725 (`MC_REVIEW_ON_WORKER`): THE OUTER BOUND WHILE THE FINAL REVIEW IS ON THE
 *  WORKER. A queued review waits for a worker, then walks a whole-document model call;
 *  on a 10k-article declaration that is minutes, not the seconds the three-minute
 *  bound was sized for. While the recalculation line says a `review` job is queued or
 *  running, the wait is extended to ten minutes from its start, never shortened. */
const REVIEW_RETRY_MAX_TOTAL_ON_WORKER_MS = 600_000
/** While the run feed is WAITING for a settle it knows is coming (`run.waiting`: a
 *  review or a recalculation requested and not yet settled), the retry does not press
 *  approve on every round: each press is a whole guard pass on the server and cannot
 *  succeed before that settle. It re-arms instead, presses ONCE when the settle count
 *  moves, and otherwise at most once a minute as a heartbeat, so a feed that waits on
 *  something that will never settle (a request whose walker died) still reaches the
 *  server's own answer. */
const REVIEW_WAIT_HEARTBEAT_MS = 60_000
/** TUR-1725: A BUSY REFUSAL (`review_busy`: another writer held the declaration row, or
 *  a deadlock was lost) is retried with a growing delay and a cap, not every
 *  REVIEW_RETRY_MS. The writer holding the row can be another approve's decisive part
 *  (tens of seconds on a 10k-article declaration), and each press is a whole guard pass
 *  on the server: 10 s, 20 s, 40 s, 60 s apart (never more than 60 s), and after
 *  REVIEW_BUSY_MAX_AUTO_RETRIES automatic presses answered busy the press is the reviewer's own
 *  again (the panel's manual "busy" copy). Any other answer starts the count over. */
const REVIEW_BUSY_RETRY_BASE_MS = 10_000
const REVIEW_BUSY_RETRY_MAX_MS = 60_000
const REVIEW_BUSY_MAX_AUTO_RETRIES = 4
/** TUR-1935: how long a press remembered while the gate settles stays owed. A settle
 *  is seconds (the open-items read of a 10k-article declaration, ~15 s); one still
 *  going after a minute is no longer the press the reviewer made, so it lapses, with
 *  a toast saying so, rather than opening the confirmation out of nowhere. */
const QUEUED_PRESS_MAX_MS = 60_000
/** The delay before the next automatic press after `busyRefusals` busy answers in a row. */
const busyRetryDelayMs = (busyRefusals: number) =>
  Math.min(
    REVIEW_BUSY_RETRY_BASE_MS * 2 ** Math.max(0, busyRefusals - 1),
    REVIEW_BUSY_RETRY_MAX_MS,
  )
/** The imperative surface `useStudioV2Keyboard` drives — Enter/1/2/3 fire the
 *  SAME code path a click does (same gate, same 409 handling, same toasts),
 *  never a second copy of the approve flow. */
export interface StudioV2FooterBarHandle {
  approve: () => void
  request: () => void
  defer: () => void
  /** Weiter's terminal stop: with no open point left to jump to, focus and
   *  briefly flash the approve button. A gated approve then speaks its own
   *  refusal reason on invoke — Weiter never duplicates that copy. */
  focusApprove: () => void
}

/**
 * The bottom action bar: gate text (left) + the three actions, reordered
 * primary-first (`primaryAction`). Übernehmen is the only one of the three
 * that can be disabled — Anfordern and Überspringen are ALWAYS available,
 * the concrete expression of "the journey never dead-ends" at this bar.
 *
 * Markup follows the prototype's `.revfoot` (`.gate` + `.acts` of `.act`
 * buttons, the primary one first) — hand-rolled `<button>`s rather than the
 * shared `Button`/`FooterActionBar` components, so the shell can carry the
 * prototype's own classes. `ApproveGatePanel` stays a SIBLING above
 * `.revfoot`, never nested inside it — that row is a fixed-height flex line
 * with `justify-content:flex-end`, and the panel would collapse as a flex
 * item there.
 */
export const StudioV2FooterBar = forwardRef<
  StudioV2FooterBarHandle,
  StudioV2FooterBarProps
>(function StudioV2FooterBar(
  {
    declId,
    frameworks,
    worklist,
    openItemsStatus = null,
    primaryAction,
    nameOf,
    nextDeclarationId,
    onApproved,
    onAlreadyApproved,
    inquiryStateOf = () => "none",
    inquiryArticleCountOf = () => 0,
    inquiryIsDeadAddressOf = () => false,
    onNoNextDeclaration,
    supplierMatched = null,
    supplierId = null,
    requestContext = null,
    supplierMentioned = null,
    onReextract,
    openArticleIds = NO_ARTICLE_IDS,
    resolveOpenArticleIds,
    onGoToStatement = () => {},
    onGoToFramework = () => {},
    labelOf,
    cardOrderOf,
    bulkAction = null,
    onNextOpenPoint,
    cards = NO_CARDS,
    loadAllCards,
    summaryVerdictOf,
    focusFooter,
  },
  ref,
) {
  const compact = focusFooter !== undefined
  const { translate } = useTranslation()
  // Paged: the request dialog opens once every regulation's cards are in. Any that
  // could not be loaded are NAMED (the draft does not quote them) — never a silently
  // partial declaration. The button is busy meanwhile, so it cannot be pressed twice.
  const [requestLoading, setRequestLoading] = useState(false)
  // TUR-1902: the request dialog sends collected follow-up questions, which the
  // server refuses (403 FEATURE_DISABLED) while MC_DECLARATION_INQUIRY is off. Request
  // then goes straight to the questionnaire request, the dialog's one action that works
  // without the flag: the button never dead-ends (see the bar's own doc), and it never
  // opens a dialog whose Send cannot succeed.
  // (The flags are never "loading" here: `FeatureFlagProvider` renders nothing
  // until they are in.)
  const inquiryFlag = useFeatureFlag("MC_DECLARATION_INQUIRY")
  // Synchronous twin of `requestLoading`: a double click lands before the render
  // that disables the button.
  const requestBusyRef = useRef(false)
  const openRequestDialog = () => {
    if (!inquiryFlag) {
      // Busy while the open articles resolve, so a second press cannot navigate
      // a second time.
      if (requestBusyRef.current) return
      requestBusyRef.current = true
      setRequestLoading(true)
      void handleAnfordern().finally(() => {
        requestBusyRef.current = false
        setRequestLoading(false)
      })
      return
    }
    if (!loadAllCards) {
      setRequestDialogOpen(true)
      return
    }
    if (requestBusyRef.current) return
    requestBusyRef.current = true
    setRequestLoading(true)
    // `loadAllCards` resolves to the regulations it could not load (never the open
    // one) and does not reject; a rejection still opens the dialog, unnamed.
    void loadAllCards()
      .then(
        (missing) => missing ?? [],
        () => [] as string[],
      )
      .then((missing) => {
        requestBusyRef.current = false
        setRequestLoading(false)
        if (missing.length > 0)
          toast.warning(
            translate("MC_DECL_V2_REQUEST_LOAD_PARTIAL", {
              names: missing.map(nameOf).join(", "),
            }),
          )
        setRequestDialogOpen(true)
      })
  }
  const navigate = useNavigate()
  // The overview's own list params (TUR-1211), carried into the Skip/Approve
  // "next declaration" jump below so it walks the SAME order the reviewer
  // saw there rather than the server's arbitrary no-sort default.
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const approve = useApproveDeclaration()
  // MC_APPROVE_PRECHECK (default off: off, nothing below this flag's name ever
  // changes what runs). The run's deferred final review is asked for when a
  // deliberate press opens the confirmation, so it is usually done by the time
  // the request goes out; see `handleApprove`. Fire-and-forget: `mutate` never
  // throws, and approve's own REVIEW_PENDING gate holds if this fails.
  // STICKY FOR THIS MOUNT. Flags refresh at runtime (`FeatureFlagProvider`), and
  // a flag that went off under a wait already running would take the cancel
  // safeguards with it (the cancel would close the dialog and leave the retries
  // armed). So once this footer has seen the flag on, it keeps the behaviour until
  // it unmounts; a declaration change remounts it. Never seen on: main, untouched.
  const precheckFlag = useFeatureFlag("MC_APPROVE_PRECHECK")
  const precheckSeenOnRef = useRef(false)
  if (precheckFlag) precheckSeenOnRef.current = true
  const precheckOn = precheckSeenOnRef.current
  const { mutate: startFinalReview } = useReviewNow()

  // Stage 1 (2026-09-17 decision): open supplier question batches no longer
  // gate Freigeben, they only require an explicit, reasoned acknowledgement
  // on the way through. React-query dedupes this against `useStudioV2State`'s
  // own `useDeclarationInquiries(declId)` read (same key), so this is not a
  // second network request.
  const inquiriesQ = useDeclarationInquiries(declId)
  const openBatches = openQuestionBatches(inquiriesQ.data ?? [])
  // Non-null = the dialog is open, showing exactly these batches. Two
  // sources feed it: the client's own read above (a deliberate press with
  // known open batches) and the server's own late 409 (a batch sent after
  // this page loaded, or the late re-check), see handleApprove below.
  const [ackDialogBatches, setAckDialogBatches] = useState<
    OpenQuestionBatchView[] | null
  >(null)
  // The reason the reviewer confirmed for the CURRENT press. The automatic
  // retries of that press (REVIEW_PENDING and the other waits) resend it; a
  // new deliberate press without it asks again.
  const ackReasonRef = useRef<string | null>(null)
  // The approve confirmation. Approve writes master data and seals the
  // declaration for good, so no press, from the button or the keyboard, sends
  // it without this dialog's confirm.
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Whether the CURRENT press was confirmed. Like `ackReasonRef`, it carries
  // over to that press's automatic retries and to the acknowledgement a late
  // 409 OPEN_QUESTIONS_UNACKNOWLEDGED asks for; a new deliberate press asks
  // again.
  const confirmedRef = useRef(false)
  // MC_APPROVE_PRECHECK, "checking": the confirmation stays open, reading as
  // the wait for the final review, while the wait's automatic retries run. It
  // clears ONLY on a success, a refusal that is not the wait, the wait ending
  // or a cancel: never when a retry starts or `sending` flips, which is what
  // keeps the dialog from flashing back to its counts body between retries.
  // The ref is for handlers a click or a timer can reach before the render that
  // follows the state; written only through `setChecking`.
  const [approveChecking, setApproveChecking] = useState(false)
  const approveCheckingRef = useRef(false)
  const setChecking = (checking: boolean) => {
    approveCheckingRef.current = checking
    setApproveChecking(checking)
  }
  // Whether the confirmation is open, synchronously: a second press into the
  // open dialog (the keyboard path) must not start the review a second time.
  const confirmOpenRef = useRef(confirmOpen)
  confirmOpenRef.current = confirmOpen
  // True from the instant an approve request goes out until it has answered
  // and its refusal has been handled. Cancel and Escape are refused on it: a
  // cancel taken while the request is out could still be followed by the
  // approval. A ref rather than `approve.isPending`, which a click can outrun
  // (the request is out before the render that disables the button).
  const approveRequestOutRef = useRef(false)
  // Set by a cancel in the checking state, cleared by the next deliberate
  // press. Read at SEND time by the automatic retry: a cancel that lands in
  // the tick the retry timer fires would otherwise lose to the callback that
  // effect cleanup has not yet removed.
  const waitCancelledRef = useRef(false)

  // TUR-1211 (W5-1): the collected-questions counter and request modal.
  // Independent of `primaryAction`/`handleAnfordern` above: a reviewer can
  // have open questions regardless of which single action ("accept"/
  // "request"/"defer") is primary right now.
  const { data: questionDrafts } = useDeclarationQuestions(declId, false)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const draftCount = questionDrafts?.length ?? 0
  // Keyed on the framework slug itself, `null` included (rather than
  // skipped): a draft raised free-standing on the declaration, with no
  // framework/statement, still counts toward the button's own `draftCount`
  // badge, so leaving it out here made the tooltip's per-group sum disagree
  // with that total. Kept as its own group, labelled "No framework" below,
  // so the two numbers always add up the same way (W5-1 fix).
  const draftFrameworkCounts = new Map<string | null, number>()
  for (const q of questionDrafts ?? []) {
    draftFrameworkCounts.set(
      q.framework,
      (draftFrameworkCounts.get(q.framework) ?? 0) + 1,
    )
  }
  const requestTooltip = Array.from(draftFrameworkCounts.entries())
    .map(
      ([fw, n]) =>
        `${fw ? nameOf(fw) : translate("MC_DECL_V2_REQUEST_NO_FRAMEWORK")} ${n}`,
    )
    .join(" · ")
  const [gateDetail, setGateDetail] = useState<ApproveGateDetail | null>(null)
  // The run feed, armed ONLY by a REVIEW_PENDING refusal (no `watchOnMount`):
  // a footer that polled the run from mount would add a background request
  // loop to every declaration for a wait that most never hit.
  const run = useRunPoll(declId)
  // A2-1b-2: the recalculation, said next to Approve — on the worker from its job
  // rows, in the API from the run's own bookends (the approve guard's read).
  const recompute = useRecomputeStatus(declId).data
  const recomputeShown = !!recompute && recompute.status !== "idle"
  // A RECOMPUTE_RUNNING/DROPPED refusal outlives the automatic wait (three minutes,
  // REVIEW_RETRY_MAX_TOTAL_MS), and its manual copy says "in a moment" — so when
  // the walk ends after that, nothing on screen changes and the reviewer has no
  // way to know (prod 2026-10-19: it ended 90 s after the wait gave up, and the
  // declaration stayed unapproved). Track whether the walk was SEEN open since
  // this refusal, and say it is over once it reads idle again. Seen-open is
  // required: an idle line beside a refusing guard (a request on a dead worker)
  // means unknown, and announcing "finished" there would send the reviewer
  // straight into the same refusal. Adjusted during render, not in an effect.
  const waitingOnRecompute =
    gateDetail?.code === "RECOMPUTE_RUNNING" ||
    gateDetail?.code === "RECOMPUTE_DROPPED"
  const recomputeOpen =
    recompute?.status === "queued" || recompute?.status === "running"
  const [sawRecomputeOpen, setSawRecomputeOpen] = useState(false)
  if (!waitingOnRecompute && sawRecomputeOpen) setSawRecomputeOpen(false)
  if (waitingOnRecompute && recomputeOpen && !sawRecomputeOpen) {
    setSawRecomputeOpen(true)
  }
  const recomputeFinished =
    waitingOnRecompute && sawRecomputeOpen && recompute?.status === "idle"
  // TUR-1725: the final review is a job on the worker right now (queued or walking).
  const reviewOnWorker = recomputeOpen && recompute?.kind === "review"
  // The run feed, read from timer callbacks armed renders ago: refs, updated during
  // render like `approveIsPendingRef` below.
  const runWaitingRef = useRef(false)
  runWaitingRef.current = run.waiting === true
  const runSettledRef = useRef(run.settledCount)
  runSettledRef.current = run.settledCount
  // The last approve this footer SENT, and the settle count it was sent at: a
  // waiting feed presses again only once that count moves (or the heartbeat).
  const pressedAtRef = useRef(0)
  const settledAtPressRef = useRef(run.settledCount)
  // True while an automatic retry is still owed — drives both the gate
  // panel's wording and the accept button's spinner, so the reviewer can see
  // that the second press is being made for them.
  const [awaitingReview, setAwaitingReview] = useState(false)
  // TUR-1935: a deliberate press made while the gate was only settling (see
  // `gateSettling`), owed until it settles; and when it was made (QUEUED_PRESS_MAX_MS).
  const [pressQueued, setPressQueued] = useState(false)
  const pressQueuedAtRef = useRef<number | null>(null)
  // TUR-1915: an automatic press is out AFTER the run feed reported the awaited
  // review settled: the screen says "Approving …" rather than "still running".
  const [approvingAfterSettle, setApprovingAfterSettle] = useState(false)
  // Idle rounds spent (see REVIEW_RETRY_MAX_IDLE_ROUNDS). A ref, not state:
  // it must not itself re-run the retry effect, only bound it.
  const reviewIdleRounds = useRef(0)
  // Busy answers (`review_busy`) in a row (see REVIEW_BUSY_RETRY_BASE_MS): stretches
  // the next automatic press and caps how many are made.
  const busyRefusals = useRef(0)
  // When the current wait must end no matter what the run feed says (see
  // REVIEW_RETRY_MAX_TOTAL_MS). Set ONCE, on the transition into waiting:
  // a later refusal must never push it out, which is the whole difference
  // between this bound and the idle-round one.
  const reviewDeadline = useRef<number | null>(null)
  // When the current wait began (its deadline was set), so an extension is measured
  // from the same start; and a counter the outer bound's timer re-arms on.
  const reviewWaitStartedAt = useRef<number | null>(null)
  const [reviewDeadlineVersion, setReviewDeadlineVersion] = useState(0)
  /** Has the current wait's window closed? A deadline left STANDING is what
   *  makes an expired wait stay expired AND keeps an ended-but-unexpired one
   *  inside its original window: it is cleared in exactly two places, a
   *  deliberate press and a successful approval, which are the only two
   *  events that may start a new window. Never in a refusal branch, however
   *  that refusal leaves the wait: a refusal is an answer to the wait that
   *  is already running. */
  const reviewWindowClosed = () =>
    reviewDeadline.current !== null && Date.now() >= reviewDeadline.current
  /** Enters (or stays in) the wait, and says whether waiting is still
   *  allowed. The deadline is set ONCE, on the transition in: every later
   *  refusal of the same wait finds it already set and leaves it where it
   *  is, which is the whole difference between this bound and the
   *  idle-round one. */
  const enterReviewWait = () => {
    if (reviewDeadline.current === null) {
      reviewWaitStartedAt.current = Date.now()
      reviewDeadline.current =
        reviewWaitStartedAt.current + REVIEW_RETRY_MAX_TOTAL_MS
      return true
    }
    return !reviewWindowClosed()
  }
  /** Ends any wait in progress and starts BOTH retry budgets over: a
   *  deliberate press, and a cancel of the checking dialog. */
  const resetReviewWait = () => {
    setAwaitingReview(false)
    reviewIdleRounds.current = 0
    busyRefusals.current = 0
    reviewDeadline.current = null
    reviewWaitStartedAt.current = null
    setGateDetail(null)
  }
  /** An automatic retry was answered by the acknowledgement dialog's refusal:
   *  the wait ends, and no callback armed before it may send (see
   *  `waitCancelledRef`; the next deliberate press that sends clears it). */
  const endWaitForHandOver = () => {
    resetReviewWait()
    waitCancelledRef.current = true
  }
  // Bumped by every auto attempt that did not finish the job, so the effect
  // below re-arms even when neither `inFlight` nor `settledCount` moved.
  const [reviewTick, setReviewTick] = useState(0)
  // `useImperativeHandle` below memoizes the handle object on a dep array
  // that deliberately omits `approve` (a new object every render) — so
  // `ref.current.approve` can stay pinned to a `handleApprove` closure from
  // an EARLIER render, one whose captured `approve.isPending` is frozen at
  // whatever it was then. A ref sidesteps that: it is the SAME object across
  // every render, updated in the render body (before any stale closure
  // could fire), so `approveIsPendingRef.current` reads correctly even from
  // a handle the imperative-handle memo never refreshed. Without this, the
  // "1" keyboard shortcut could fire a second `mutateAsync` while the button
  // itself sat `aria-disabled` mid-approval.
  const approveIsPendingRef = useRef(approve.isPending)
  approveIsPendingRef.current = approve.isPending
  // Weiter's approve terminal (D6): the accept button's DOM node to focus,
  // plus a transient flash so the jump is visible even when focus styling
  // alone would be easy to miss. The flash is IMPERATIVE classList work, not
  // state: a transient animation class needs a reflow between remove and
  // re-add to restart mid-flash, and a state-driven class can neither do
  // that nor stop its timer from setState-ing an unmounted component.
  const acceptButtonRef = useRef<HTMLButtonElement | null>(null)
  // Compact's own primary before the active Verordnung is geprueft: see
  // `compactConformNext`'s own doc below. Weiter's terminal (`focusApprove`)
  // needs a DOM node to land on here too: while this is true the accept
  // button above is not even rendered (`order`'s own filter), so a hard-
  // coded `acceptButtonRef` there would have nothing to focus and fall
  // through to the "all checked" toast, which is false in exactly this
  // state (fix wave, TUR-1211).
  const conformNextButtonRef = useRef<HTMLButtonElement | null>(null)
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    },
    [],
  )
  // Moved up from beside `order` below so `focusApprove` (declared next) can
  // read it: a separate element from `buttonOf("accept")`, so this also
  // drops the built-in Approve from `order` in exactly that state, without a
  // third branch inside `buttonOf`.
  const compactConformNext =
    focusFooter !== undefined && !focusFooter.activeFrameworkGeprueft

  // No framework extracted at all: an empty worklist there is vacuous, never
  // "all checked" — see `declarationGeprueft`'s own doc.
  const noFrameworks = frameworks.length === 0
  // "SPEICHERT ..." HONESTY: every edit (verdict, Geltung, condition field,
  // confirm) round-trips through the orion recompute and only the REFETCHED
  // read model flips geprueft/Freigeben: for a second or three the reviewer's
  // click looks ignored (product owner, 30.08.: "dauert dann einen Moment").
  // While any MC mutation or this declaration's reads are in flight the gate
  // line says so, and approve waits: clicking into the stale window would
  // only earn a 409 PENDING_ITEMS from the server anyway.
  // Scoped to THIS declaration (Copilot, #2041): an unfiltered
  // useIsMutating() counts every mutation in the app. The MC mutation hooks
  // carry the declaration id in their variables (declarationId, declId or a
  // bare id string); anything that slips the net still surfaces through the
  // read-scoped refetch below, because every MC write invalidates this
  // declaration's reads on success.
  const mutating =
    useIsMutating({
      predicate: (m) => {
        // The final review the confirmation starts carries this declaration's
        // id too, and is no save: it must not read as "saving". Flag-gated: the
        // studio's own quiet review (same hook) keeps counting as on main.
        if (precheckOn && isReviewNowMutation(m.options.mutationKey)) {
          return false
        }
        const v = m.state.variables as
          | { declarationId?: string; declId?: string }
          | string
          | undefined
        if (typeof v === "string") return v === declId
        return v?.declarationId === declId || v?.declId === declId
      },
    }) > 0
  // Only a refetch a WRITE caused counts (prod test 2026-10-02, F13): an
  // invalidated read keeps `isInvalidated` until its refetch lands, which is
  // exactly the stale window above. A read refetching on its own (a window
  // refocus, a remounting observer, the first PDF download) moves nothing,
  // yet it used to show "Saving …" and hold Approve, for the whole ~12 s
  // read-model of a 10k-article declaration.
  const refetching =
    useIsFetching({
      queryKey: ["mc", "declarations", declId],
      predicate: (query) => query.state.isInvalidated,
    }) > 0
  const saving = mutating || refetching
  const geprueft = declarationGeprueft(worklist, frameworks)
  // Only a sent Rückfrage WITHOUT articles blocks Freigeben: one that names its
  // articles holds exactly those back on approve (server `_InquiryHoldback`), so
  // it is no open point either, just the count the gate line reports.
  const sentFrameworks = frameworks.filter(
    (fw) => inquiryStateOf(fw) === "sent" && inquiryArticleCountOf(fw) === 0,
  )
  const heldArticleCount = frameworks
    .filter((fw) => inquiryStateOf(fw) === "sent")
    .reduce((sum, fw) => sum + inquiryArticleCountOf(fw), 0)
  const sentFramework = sentFrameworks[0]
  // A dead address blocks through `sentFrameworks` above (the server reports it
  // as holding nothing back, so its count is 0), but the reason it blocks is
  // NOT that it names no articles: it commonly names several and can simply
  // never be answered. Kept as its own lookup so the gate can say which of the
  // two it is, first because it is the one the reviewer can act on.
  const deadAddressFramework = sentFrameworks.find((fw) =>
    inquiryIsDeadAddressOf(fw),
  )
  const supplierUnmatched = supplierMatched === false
  // D1's map counts EVERY Freigeben blocker, not only worklist issues — so
  // the counter can stay visible (supplier missing, Rückfrage out) even when
  // `geprueft` alone would already read "all checked".
  const openPoints = countOpenPoints({
    worklist,
    supplierMatched,
    sentFrameworks,
  })
  // TUR-1563: split "genuinely blocked" from "disabled". `saving` is left
  // out of `approveBlocked` on purpose: it is a transient round-trip window
  // after every edit (the recompute + refetch, see "SPEICHERT..." above),
  // never a blocker in its own right. Folding it into the styling decision
  // would flicker the primary look off and back on every single save.
  // C1-d: never approvable on a missing or failed server answer (blocked), nor
  // while a newer one is on its way after a gesture (only disabled, like
  // `saving`, for the same no-flicker reason).
  const openItemsMissing = openItemsUnsettled(openItemsStatus)
  const openItemsRefreshing = openItemsStatus === "refreshing"
  const approveBlocked =
    supplierUnmatched || !geprueft || !!sentFramework || openItemsMissing
  const approveDisabled = approveBlocked || saving || openItemsRefreshing
  // TUR-1935: the gate is only SETTLING — a save, its refetch or the server's open
  // items are on their way — and nothing that the settle cannot change (supplier,
  // Rückfrage, no regulations) shuts it. A deliberate press in this window is
  // remembered and continued once it settles, instead of being dropped.
  const gateSettling =
    (saving || openItemsRefreshing || openItemsStatus === "pending") &&
    !supplierUnmatched &&
    !sentFramework &&
    !noFrameworks
  // The exact branch order `approveDisabledReason` below reaches its
  // "open work" copy through, reused so the first-open-point line only
  // computes (and only renders) for THAT reason, never for the supplier/
  // inquiry/no-frameworks ones, which name their own blocker already.
  // Not while the server's answer is missing, failed or being replaced: the line
  // would name an open point from data that may no longer hold.
  const openWorkBlocked =
    !supplierUnmatched &&
    !sentFramework &&
    !noFrameworks &&
    !geprueft &&
    !openItemsMissing &&
    !openItemsRefreshing
  const approveDisabledReason = supplierUnmatched
    ? translate("MC_DECL_V2_SUP_APPROVE_DISABLED")
    : deadAddressFramework
      ? translate("MC_DECL_V2_FOOTER_APPROVE_DISABLED_INQUIRY_DEAD")
      : sentFramework
        ? translate("MC_DECL_V2_FOOTER_APPROVE_DISABLED_INQUIRY")
        : openItemsStatus === "unknown"
          ? translate("MC_DECL_V2_FOOTER_OPEN_ITEMS_UNKNOWN")
          : openItemsStatus === "pending" || openItemsRefreshing
            ? translate("MC_DECL_V2_FOOTER_OPEN_ITEMS_PENDING")
            : noFrameworks
              ? translate("MC_DECL_V2_NO_FRAMEWORKS")
              : !geprueft
                ? translate("MC_DECL_V2_FOOTER_APPROVE_DISABLED_OPEN_WORK")
                : undefined
  // The FIRST open point, named exactly as the open-points popover's own
  // first row would: same framework order (`openWorkByFramework`'s own
  // worklist-first order), same in-framework order and reason copy
  // (`issuesInDisplayOrder`, `issueReasonKey`) the popover and Weiter both
  // already use, no new ordering or reason logic here. `labelOf`/
  // `cardOrderOf` fall back to the SAME defaults `OpenPointsPopover` uses
  // (imported from there) rather than a second copy that could drift.
  // `verdictOf` (TUR-1676) words a statement's step 3 by its verdict, for
  // this line, the map and the gate alike; a statement `cards` does not hold
  // resolves nothing and keeps the substances wording.
  const verdictOf = (statementId: string) =>
    summaryVerdictOf?.(statementId) ??
    cards.find((c) => c.id === statementId)?.verdict
  const reasonOf = (issue: WorklistIssue, statementId: string) =>
    openPointDetailText(
      issue.step === 3
        ? cards.find((c) => c.id === statementId)?.openDetail
        : null,
      issue.framework,
      translate(issueReasonKey(issue, verdictOf(statementId)).label),
      translate,
    )
  const firstOpenPoint = (() => {
    if (!openWorkBlocked) return null
    const byFramework = openWorkByFramework(worklist)
    const firstFramework = inFrameworkOrder(
      [...byFramework.keys()],
      frameworks,
    )[0]
    if (!firstFramework) return null
    const issue = issuesInDisplayOrder(
      byFramework.get(firstFramework) ?? [],
      (cardOrderOf ?? defaultCardOrderOf)(firstFramework),
    )[0]
    if (!issue) return null
    if (issue.unit === "statement" && issue.statementId) {
      const label = translate("MC_DECL_V2_UNIT_LABEL", {
        i: (labelOf ?? defaultLabelOf)(
          firstFramework,
          issue.statementId,
          issue.groupIndex,
        ),
      })
      return `${nameOf(firstFramework)} · ${label} · ${reasonOf(issue, issue.statementId)}`
    }
    return `${nameOf(firstFramework)} · ${translate("MC_DECL_V2_OPEN_POINTS_REFLIST")}`
  })()
  // TUR-1915: THE WAIT IS HELD UP BY A BLOCKER ON THIS SCREEN. The automatic retry
  // of a REVIEW_PENDING / RECOMPUTE_* refusal sends nothing while the gate is shut
  // here (open work that the settled review or an edit brought back, a supplier, a
  // Rückfrage), so the panel's "still running, approves automatically" copy is no
  // longer the reason: the panel and the line say the real one. The wait itself is
  // left as it is: the blocker can clear and the retry then presses as before.
  const waitBlockedLocally =
    awaitingReview &&
    !saving &&
    approveBlocked &&
    !!approveDisabledReason &&
    (gateDetail?.code === "REVIEW_PENDING" ||
      gateDetail?.code === "RECOMPUTE_RUNNING" ||
      gateDetail?.code === "RECOMPUTE_DROPPED")
  // TUR-1915: the approval request is out after the awaited review settled.
  const approving = approvingAfterSettle && approve.isPending
  // TUR-1680: while an approve press is out, the gate line says so instead of
  // the generic "Saving …" the approve request itself triggers (it is one of
  // this declaration's mutations): the request in flight, and the
  // REVIEW_PENDING wait the automatic retries sit out between requests. Not
  // the other waits (PENDING_ITEMS and its neighbours): those wait on the
  // reviewer's own work, which the line's open-points map names.
  // TUR-1915: and not while the wait is held up by a blocker on this screen (see
  // `waitBlockedLocally`): the line then shows that blocker's map instead.
  const approveInProgress =
    approve.isPending ||
    (awaitingReview &&
      gateDetail?.code === "REVIEW_PENDING" &&
      !waitBlockedLocally)
  // Hide the Approve button entirely for users without master-data-edit +
  // declarations-review (see useApproveDeclaration's compound guard). Backend
  // + guarded mutation net still enforce, but the workflow-level "approve"
  // gesture is a lie for a viewer and shouldn't appear.
  const canApprove = approve.canRun

  const goToNextOrList = () => {
    if (nextDeclarationId) {
      navigate(
        `${DECLARATIONS_BASE}/${nextDeclarationId}/review${nextDeclarationSearch(searchParams)}`,
      )
    } else if (onNoNextDeclaration) {
      onNoNextDeclaration()
    } else {
      navigate(DECLARATIONS_BASE)
    }
  }

  /** `auto` marks the retry the REVIEW_PENDING wait fires for the reviewer —
   *  same code path as a press, but it never speaks unless something the
   *  reviewer did not ask about has changed, and it never resets the wait it
   *  is itself part of. `ack` is the reason text from a confirmed
   *  `OpenQuestionsAckDialog`: its presence skips the batches check below
   *  instead of reopening the dialog that produced it, and the automatic
   *  retries of that press carry it on (`ackReasonRef`). `confirmed` comes
   *  from `ApproveConfirmDialog`'s own confirm, the only call that may send
   *  a press nobody has confirmed yet (`confirmedRef`). */
  const handleApprove = async (options?: {
    auto?: boolean
    ack?: string
    confirmed?: boolean
  }) => {
    const auto = options?.auto === true
    // A cancelled wait sends nothing more, even from a timer callback armed
    // before the cancel (see `waitCancelledRef`).
    if (auto && waitCancelledRef.current) return
    // CHECKED IMMEDIATELY BEFORE THE MUTATION, not only where the retry is
    // scheduled: a `setTimeout` is delivered late (a backgrounded tab
    // throttles it to about once a minute, a suspended one delivers it on
    // resume), so a callback armed inside the window can arrive long after
    // it closed. The wait ends here rather than sending an approve nobody
    // is waiting for any more.
    if (auto && reviewWindowClosed()) {
      setAwaitingReview(false)
      return
    }
    // ONE approve at a time, and a press that hits this guard is TOLD so.
    // The deadline above ends the WAIT, never the request: `apiClientV2` sets
    // no request timeout, so the gate panel can be back to its manual copy
    // while the automatic retry is still outstanding. The press that copy
    // invites then lands here, and returning silently is what made the button
    // read as dead. Sending a second approve is not the alternative — that is
    // the double submit this guard exists for, and resetting the mutation
    // would not avoid it either (`reset()` does not cancel the request, and a
    // React Query mutation is handed no abort signal). So the press is
    // answered rather than served. Automatic retries stay silent: nobody
    // asked for them.
    // MC_APPROVE_PRECHECK: `approveIsPendingRef` mirrors the mutation at RENDER
    // time, so two submissions before the re-render (double click, key repeat)
    // both pass it. `approveRequestOutRef` is written synchronously, so it is the
    // one that makes "one request at a time" true; without it the boolean could
    // describe request B while A is still out, and Cancel would be accepted
    // with an approval still on its way. Silent in that window: the toast
    // answers a press the screen invited, and a double click landed before
    // the screen could show the request at all.
    if (approveIsPendingRef.current) {
      if (!auto) toast.error(translate("MC_DECL_V2_APPROVE_IN_FLIGHT"))
      return
    }
    if (precheckOn && approveRequestOutRef.current) return
    if (options?.ack) ackReasonRef.current = options.ack
    else if (!auto) ackReasonRef.current = null
    const ack = ackReasonRef.current ?? undefined
    // A call carrying `ack` continues the press its acknowledgement belongs
    // to, so it keeps that press's confirmation (or the lack of one).
    if (options?.confirmed) confirmedRef.current = true
    else if (!auto && !options?.ack) confirmedRef.current = false
    // The button is `aria-disabled`, never natively `disabled` (a keyboard
    // user must be able to TAB to it and hear why it is blocked — a native
    // `disabled` button drops out of the tab order entirely). So a click OR
    // the keyboard path (1, or Enter on a single-Verordnung declaration) can
    // both reach here with the gate shut; either way this is the one guard,
    // and it says the SAME content-framed reason the tooltip already shows,
    // out loud instead of swallowed.
    if (approveDisabled) {
      if (auto) {
        // MID-SAVE IS NOT A REFUSAL. The settled review re-publishes, and
        // this declaration's reads refetch straight after — `saving` is true
        // for exactly that window, so waiting one more round is the whole
        // correction. Spending the round (a `reviewTick` bump) rather than
        // returning silently is what keeps the effect below re-arming.
        if (saving) setReviewTick((n) => n + 1)
        // MC_APPROVE_PRECHECK: the gate shut for real (the settled review's
        // findings landed on the mask), so the checking dialog must not keep
        // promising an automatic approval over it. Only the dialog gives way:
        // the wait itself is left exactly as main has it.
        if (precheckOn && !saving && approveCheckingRef.current) {
          setChecking(false)
          setConfirmOpen(false)
        }
        // Not saving, still disabled: fixable blockers (open work, running
        // review, unmatched supplier) can clear at any moment via a mutation
        // in another tab or the pane the reviewer just finished. Stay in the
        // wait silently — the effect re-fires every REVIEW_RETRY_MS until the
        // idle-round budget exhausts, then closes the gate itself.
        return
      }
      // TUR-1935: only settling (a save, its refetch, the open-items read on the
      // way): remember the press and continue it once settled (the effect below),
      // instead of dropping it. Never a press from the confirmation or the
      // acknowledgement: those send, and a send is never made for the reviewer.
      if (gateSettling && !options?.ack && !options?.confirmed) {
        pressQueuedAtRef.current = Date.now()
        setPressQueued(true)
        return
      }
      if (approveDisabledReason) {
        // The gate shut while the confirmation stood open (work landed from
        // another tab): its reason must not be read behind the modal.
        setConfirmOpen(false)
        toast.error(approveDisabledReason)
      }
      return
    }
    // Stage 1: an open supplier question batch no longer refuses approve, it
    // opens the acknowledgement dialog INSTEAD of sending the request, unless
    // `ack` already carries the reason that dialog produced (its confirm
    // re-fires this same function with `ack` set, and must not loop back into
    // itself). Checked after the disabled guard above so a REAL blocker (open
    // work, unmatched supplier) still wins first.
    if (!ack && openBatches.length > 0) {
      // A batch that turned up while the checking dialog waits: the reason is
      // the reviewer's to give, so the dialog gives way (never two modals).
      if (auto && approveCheckingRef.current) {
        setChecking(false)
        setConfirmOpen(false)
      }
      // The wait hands over to the reviewer and ENDS: left running, a cancel of
      // the acknowledgement would not stop retries that could approve later.
      // The acknowledgement's confirm is a deliberate press and starts anew.
      if (precheckOn && auto) endWaitForHandOver()
      setAckDialogBatches(openBatches.map(localBatchView))
      return
    }
    // Then the confirmation, after the acknowledgement when one was asked
    // for. Automatic retries never get here unconfirmed: they only follow a
    // request, and only a confirmed press sends one.
    if (!auto && !confirmedRef.current) {
      setAckDialogBatches(null)
      // The final review starts WITH the confirmation, once per opening and
      // never on a retry, so the refusal that would have started it on the way
      // out is usually not needed.
      // Not beside a walking cone: the server starts the final review only after
      // its other guards pass (open inquiry, unsettled recompute, then review),
      // and a review started ahead of that order reads a state the cone is about
      // to change. `unlessRecomputing` makes the SERVER refuse the early start
      // while a recalculation is unsettled (it sees every process; this footer's
      // own run feed is armed only by a wait refusal, so it cannot). Refused,
      // nothing else changes: approve answers RECOMPUTE_RUNNING or REVIEW_PENDING
      // and the checking dialog covers the wait. (A save in flight cannot reach
      // this point: `approveDisabled` already returned above.)
      if (precheckOn && !confirmOpenRef.current) {
        startFinalReview({ declarationId: declId, unlessRecomputing: true })
      }
      confirmOpenRef.current = true
      setConfirmOpen(true)
      return
    }
    if (!auto) {
      // A deliberate press outranks any wait in progress and starts BOTH
      // retry budgets over.
      resetReviewWait()
      waitCancelledRef.current = false
    }
    approveRequestOutRef.current = true
    pressedAtRef.current = Date.now()
    settledAtPressRef.current = runSettledRef.current
    try {
      const result = await approve.mutateAsync(
        ack
          ? { declId, acknowledgeOpenQuestions: true, reason: ack }
          : { declId },
      )
      setAwaitingReview(false)
      // An automatic retry skips the press-only clear above, so the refusal it
      // answered is cleared here: left set, the gate panel stays under the
      // success dialog, flipped to its manual "try again" copy.
      setGateDetail(null)
      reviewIdleRounds.current = 0
      busyRefusals.current = 0
      ackReasonRef.current = null
      confirmedRef.current = false
      setAckDialogBatches(null)
      setConfirmOpen(false)
      setChecking(false)
      reviewDeadline.current = null
      reviewWaitStartedAt.current = null
      onApproved(result)
    } catch (err) {
      const detail = approveGateDetailOf(err)
      // Busy answers in a row (REVIEW_BUSY_RETRY_BASE_MS); any other answer starts over.
      busyRefusals.current = detail?.review_busy ? busyRefusals.current + 1 : 0
      const isReviewWait =
        // TUR-1725: a DELAYED final review (`review_delayed`: its job sits on the
        // worker queue and no worker is taking it) is not a wait with an end the
        // screen can sit out: retrying would only earn the same refusal. The panel
        // says so (`ApproveGatePanel`) and the next press is the reviewer's own.
        (detail?.code === "REVIEW_PENDING" && !detail.review_delayed) ||
        // A gesture's own recalculation is still running, or was dropped and has
        // just been restarted by the server: the same named wait with an end as the
        // final review, so the retry presses again for the reviewer.
        detail?.code === "RECOMPUTE_RUNNING" ||
        detail?.code === "RECOMPUTE_DROPPED"
      // The server may have just re-fired a recalculation (DROPPED), or one is
      // running: the recalculation line asks for its status now rather than
      // keeping a stale "failed · Try again" (A2-1b-2).
      if (
        detail?.code === "RECOMPUTE_RUNNING" ||
        detail?.code === "RECOMPUTE_DROPPED" ||
        // TUR-1725: a final review queued on the worker shows on the same line
        // (`kind: "review"`), and that line is what extends the wait's bound.
        (detail?.code === "REVIEW_PENDING" && detail.review_started)
      ) {
        announceRecompute(declId)
      }
      // With MC_APPROVE_PRECHECK the wait is the one refusal the open
      // confirmation sits out, in its checking state (set in the branch below).
      const keepsDialog = precheckOn && isReviewWait && confirmOpenRef.current
      // Whatever the other refusals, the surface explaining them (gate panel,
      // toast, acknowledgement dialog) must not sit under the confirmation.
      if (!keepsDialog) {
        setConfirmOpen(false)
        setChecking(false)
      }
      // Any other refusal is explained by the gate panel, which the open
      // dialog would otherwise cover.
      if (detail?.code !== "OPEN_QUESTIONS_UNACKNOWLEDGED") {
        setAckDialogBatches(null)
      }
      if (detail?.code === "OPEN_QUESTIONS_UNACKNOWLEDGED") {
        // MUST be checked BEFORE the itemised gate-panel routing below (the
        // `OPEN_INQUIRY` branch and its neighbours): this refusal has its
        // own dedicated surface (the SAME dialog the proactive check above
        // opens) and must never land in `ApproveGatePanel`, never set
        // `awaitingReview`, and never join the auto-retry list: arming that
        // retry would re-fire approve every few seconds underneath a dialog
        // that is waiting on the reviewer to type a reason.
        if (precheckOn && auto) endWaitForHandOver()
        setAckDialogBatches((detail.open_questions ?? []).map(serverBatchView))
      } else if (detail?.code === "ALREADY_APPROVED") {
        // Someone else approved this declaration between this reviewer's
        // last read and this click — the gate panel has nothing itemised to
        // say about that (no blocking work, no inquiry), so this names it
        // directly instead. Refetches every declaration-scoped read so the
        // worklist/statements this reviewer is looking at stop disagreeing
        // with the server, THEN (if wired) flips the workspace — otherwise
        // the reviewer stays parked in an editable mask on a declaration
        // that is already approved server-side (the server itself still
        // accepts post-approval statement writes, a known pre-existing
        // defect out of this fix's scope — the FE must not rely on it
        // rejecting a stray edit).
        // TWO DISTINCT MESSAGES, not one reused for both callers.
        // `MC_DECL_V2_ALREADY_APPROVED`'s own copy promises "Request and
        // Skip stay available while its data refreshes" — true only when
        // NOBODY flips the workspace afterwards (every caller that predates
        // `onAlreadyApproved`, and every test that omits it). Once a caller
        // DOES wire it (`StudioV2Layout`, in production), that promise
        // becomes false the instant this branch returns: the footer this
        // very toast names is about to unmount. A separate key covers that
        // path instead, making no claim about controls that are leaving.
        toast.error(
          translate(
            onAlreadyApproved
              ? "MC_DECL_V2_ALREADY_APPROVED_MOVING_ON"
              : "MC_DECL_V2_ALREADY_APPROVED",
          ),
        )
        // Excludes the document blob (`.../document`): `onAlreadyApproved`
        // (when wired) unmounts this whole workspace right after, so
        // re-downloading the source PDF one beat before that happens would
        // be pure waste; when it is NOT wired (no prop passed) the review
        // mask stays mounted and the exclusion is exactly the pre-existing
        // behaviour (no PDF re-fetch either way).
        queryClient.invalidateQueries({
          queryKey: ["mc", "declarations"],
          predicate: (query) => query.queryKey[3] !== "document",
        })
        setAwaitingReview(false)
        onAlreadyApproved?.()
      } else if (isReviewWait) {
        // The named wait with an end (see REVIEW_RETRY_MS's own doc): the
        // panel says what is being waited for, the run feed says when it is
        // over, and the retry effect below presses again so the reviewer
        // does not have to.
        setGateDetail(detail)
        // A busy row is retried with a growing delay, and only so often
        // (REVIEW_BUSY_MAX_AUTO_RETRIES; counted above).
        const busyCapped = busyRefusals.current > REVIEW_BUSY_MAX_AUTO_RETRIES
        // A REFUSAL THAT LANDS AFTER THE WINDOW CLOSED IS NOT A NEW WAIT.
        // The request can outlive the deadline (`apiClientV2` sets no
        // request timeout), and answering it by re-entering the wait would
        // hand the loop back the three minutes it just spent. The panel
        // stands with its manual copy instead, and no background run poll
        // is armed for a wait that is over.
        const stillWaiting = enterReviewWait() && !busyCapped
        setAwaitingReview(stillWaiting)
        if (keepsDialog) {
          // A wait that is already over has nothing to cover: the dialog
          // closes onto the gate panel, as on main.
          setChecking(stillWaiting)
          if (!stillWaiting) setConfirmOpen(false)
        }
        if (stillWaiting) {
          // A recalculation's end shows on the run feed as its settled
          // bookend, so watch it for RECOMPUTE_* the same as a started review.
          if (detail.review_started || detail.code !== "REVIEW_PENDING")
            run.watch()
          setReviewTick((n) => n + 1)
        }
      } else if (
        detail?.code === "PENDING_ITEMS" ||
        detail?.code === "OPEN_INQUIRY" ||
        detail?.code === "MISSING_LIST_VERSION" ||
        detail?.code === "NO_SUPPLIER"
      ) {
        // Same wait shape as REVIEW_PENDING, but the block clears from the
        // reviewer's own actions (finishing a statement, an inquiry landing,
        // picking a list version, assigning a supplier) rather than a
        // backend-run finishing. The retry effect below fires every
        // REVIEW_RETRY_MS; the moment `approveDisabled` flips false locally,
        // handleApprove({ auto: true }) sends the mutation and the reviewer
        // never has to press the button a second time.
        setGateDetail(detail)
        // NOTHING WILL CHANGE ON ITS OWN, so nothing is waited for: a
        // PENDING_ITEMS with an EMPTY `blocking_work` and no cone walking
        // refuses on a counter (`pending`) the reviewer has to go and fix by
        // hand, the panel now names it. Retrying that every five seconds is
        // a busy loop against a server that will refuse identically every
        // time (Vishay, 16.09: an hour of them). The panel stands, the
        // button stays the reviewer's own press. Scoped to PENDING_ITEMS:
        // the other three codes in this branch DO clear from work landing
        // elsewhere (a reply, a list version, a supplier), so they keep
        // their wait.
        const nothingToWaitFor =
          detail.code === "PENDING_ITEMS" &&
          (detail.blocking_work ?? []).length === 0 &&
          !run.inFlight
        if (nothingToWaitFor) {
          // The wait ends, but its WINDOW STANDS. Clearing the deadline here
          // would hand the next late answer a blank slate: `enterReviewWait`
          // reads "no deadline" as "no wait yet" and opens a fresh three
          // minutes, which is the very hole this bound exists to close. Only
          // a deliberate press or a successful approval may start a new
          // window, and both clear it where they happen.
          setAwaitingReview(false)
        } else {
          const stillWaiting = enterReviewWait()
          setAwaitingReview(stillWaiting)
          if (stillWaiting) setReviewTick((n) => n + 1)
        }
      } else if (detail) {
        setAwaitingReview(false)
        setGateDetail(detail)
      } else {
        setAwaitingReview(false)
        // No structured 409 detail at all (a network failure, a bare 5xx) —
        // still names the SAME alternatives `ApproveGatePanel` closes every
        // itemised refusal with, so this toast-only path never dead-ends
        // either.
        toast.error(
          `${translate("MC_DECL_V2_GATE_GENERIC")} ${translate("MC_DECL_V2_GATE_ALTERNATIVES")}`,
        )
      }
    } finally {
      approveRequestOutRef.current = false
    }
  }

  /** The confirmation's cancel, which Escape reaches as well (Radix routes both
   *  through `onOpenChange`, which the dialog forwards here): so the in-flight
   *  guard lives HERE, where neither path can skip it. In the checking state a
   *  cancel also ends the wait, through the same reset a deliberate press runs. */
  const handleConfirmCancel = () => {
    if (precheckOn) {
      if (approveRequestOutRef.current) return
      if (approveCheckingRef.current) {
        waitCancelledRef.current = true
        confirmedRef.current = false
        ackReasonRef.current = null
        resetReviewWait()
        setChecking(false)
      }
    }
    setConfirmOpen(false)
  }

  // TUR-1935: THE REMEMBERED PRESS, continued once the gate settled. The same
  // deliberate press, so it opens the confirmation or says the real reason (open
  // work the refreshed read found), exactly as a press made now would.
  useEffect(() => {
    if (!pressQueued || gateSettling) return
    setPressQueued(false)
    const at = pressQueuedAtRef.current
    pressQueuedAtRef.current = null
    if (at === null) return
    // Expired before the lapse timer ran (a suspended tab resuming late): the
    // press lapses all the same, and says so, never silently.
    if (Date.now() - at > QUEUED_PRESS_MAX_MS) {
      toast.warning(translate("MC_DECL_V2_APPROVE_PRESS_LAPSED"))
      return
    }
    void handleApprove()
    // `handleApprove` is a new closure every render (see the retry effect below);
    // this render's is the one that reads the settled gate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressQueued, gateSettling])

  // ...and its lapse (QUEUED_PRESS_MAX_MS from the first press), said out loud: the
  // spinner stopping on its own would read as the press having been ignored.
  useEffect(() => {
    if (!pressQueued) return undefined
    const at = pressQueuedAtRef.current ?? Date.now()
    const timer = setTimeout(
      () => {
        pressQueuedAtRef.current = null
        setPressQueued(false)
        toast.warning(translate("MC_DECL_V2_APPROVE_PRESS_LAPSED"))
      },
      Math.max(0, at + QUEUED_PRESS_MAX_MS - Date.now()),
    )
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressQueued])

  // THE CHECKING DIALOG COVERS THE WAIT, AND ONLY THE WAIT. Answers settle it
  // in `handleApprove`; this is for what ends without one: the wait itself
  // (window closed, idle rounds spent), or the dialog closing under it.
  useEffect(() => {
    if (!approveChecking) return
    if (!awaitingReview) {
      approveCheckingRef.current = false
      setApproveChecking(false)
      setConfirmOpen(false)
    } else if (!confirmOpen) {
      approveCheckingRef.current = false
      setApproveChecking(false)
    }
  }, [approveChecking, awaitingReview, confirmOpen])

  // THE WAIT'S OWN CLOCK. Re-armed by `reviewTick` (an auto attempt that
  // changed nothing) and `settledCount` (the review re-published). Bounded,
  // and the bound releases the wait rather than silently stalling it.
  //
  // The budget is meant to count only real SILENCE — nothing saving, no run
  // walking, nothing settling — so either one resets it: a reviewer action
  // (mutation/refetch, `saving`) has a live reason to keep hoping, and so
  // does a cone that is visibly walking (`run.inFlight`, armed only on the
  // REVIEW_PENDING branch below) — pressing approve into either would only
  // earn the same refusal again, so a tick spent there must not count toward
  // the cap.
  useEffect(() => {
    if (saving || run.inFlight || run.waiting) reviewIdleRounds.current = 0
  }, [saving, run.inFlight, run.waiting])

  // TUR-1725: a final review on the worker extends the wait's outer bound to
  // REVIEW_RETRY_MAX_TOTAL_ON_WORKER_MS from the wait's own start. Only ever later,
  // and only while the wait is open (a closed window stays closed).
  useEffect(() => {
    if (!awaitingReview || !reviewOnWorker) return
    const start = reviewWaitStartedAt.current
    const deadline = reviewDeadline.current
    if (start === null || deadline === null || Date.now() >= deadline) return
    const extended = start + REVIEW_RETRY_MAX_TOTAL_ON_WORKER_MS
    if (extended > deadline) {
      reviewDeadline.current = extended
      setReviewDeadlineVersion((n) => n + 1)
    }
  }, [awaitingReview, reviewOnWorker])

  // THE OUTER BOUND'S OWN TIMER, and the reason it is one: every other clock
  // in this component is a passenger of retry progress. The retry effect
  // re-arms on its own deps, and each of them can stop moving while the wait
  // is still on screen: an auto attempt that finds approve blocked returns
  // without spending a round (no `reviewTick`), and an approve request that
  // never settles spends none either. A bound that is only ever read from
  // inside that effect is therefore not a bound at all. This one runs on
  // nothing but the wall clock.
  useEffect(() => {
    if (!awaitingReview) return undefined
    const deadline = reviewDeadline.current
    if (deadline === null) return undefined
    // The deadline is deliberately LEFT STANDING (see `reviewWindowClosed`):
    // it is what tells a late answer that its wait is over.
    const timer = setTimeout(
      () => setAwaitingReview(false),
      Math.max(0, deadline - Date.now()),
    )
    return () => clearTimeout(timer)
    // `reviewDeadlineVersion`: re-armed when the bound is extended (TUR-1725).
  }, [awaitingReview, reviewDeadlineVersion])

  useEffect(() => {
    if (!awaitingReview) return undefined
    if (reviewIdleRounds.current >= REVIEW_RETRY_MAX_IDLE_ROUNDS) {
      setAwaitingReview(false)
      return undefined
    }
    // The outer bound, checked here too so no retry is ever SCHEDULED past
    // it. The timer above is what actually ends the wait.
    if (reviewWindowClosed()) {
      setAwaitingReview(false)
      return undefined
    }
    const timer = setTimeout(
      () => {
        // AN APPROVAL IS STILL OUT (a long final write): this round sends nothing
        // and leaves `approvingAfterSettle` to that request's own completion;
        // pressing here would only hit the same `approveIsPendingRef` guard in
        // `handleApprove`, and clearing the flag would put "Checking the approval"
        // back over a request that is approving. Re-armed, not counted as silence.
        if (approveIsPendingRef.current) {
          setReviewTick((n) => n + 1)
          return
        }
        // WAITING ON THE FEED (see REVIEW_WAIT_HEARTBEAT_MS): re-arm without
        // pressing until the settle count moves or the heartbeat is due.
        const settled = runSettledRef.current !== settledAtPressRef.current
        if (
          runWaitingRef.current &&
          !settled &&
          Date.now() - pressedAtRef.current < REVIEW_WAIT_HEARTBEAT_MS
        ) {
          setReviewTick((n) => n + 1)
          return
        }
        // A heartbeat or settle press while the feed waits is not silence.
        if (!runWaitingRef.current) reviewIdleRounds.current += 1
        // TUR-1915: the awaited review settled since the last press, so this press
        // is the approval itself unless something else refuses it.
        setApprovingAfterSettle(settled)
        void handleApprove({ auto: true }).finally(() =>
          setApprovingAfterSettle(false),
        )
      },
      busyRefusals.current > 0
        ? busyRetryDelayMs(busyRefusals.current)
        : REVIEW_RETRY_MS,
    )
    return () => clearTimeout(timer)
    // `handleApprove` is deliberately not a dep — it is a new closure every
    // render, and re-running this effect on every render would restart the
    // timer forever. `approveDisabled`/`approveDisabledReason`/`saving` ARE
    // listed despite that: they are what `handleApprove`'s closure reads to
    // decide the outcome of the auto-attempt, and without them a gate change
    // (open work appears, supplier becomes unmatched, a save starts) between
    // two of the OTHER deps ticking would fire with a stale closure — an
    // approve attempt the UI no longer allows, or a toast for a reason that
    // no longer applies. All three are primitives (boolean/string|undefined),
    // so listing them re-arms the timer on a real gate change only, not on
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    awaitingReview,
    run.inFlight,
    run.waiting,
    run.settledCount,
    reviewTick,
    approveDisabled,
    approveDisabledReason,
    saving,
  ])

  const handleAnfordern = async () => {
    // MC_DECL_V2_SUP: an unmatched supplier means the server 422s NO_SUPPLIER
    // on the very endpoint this would navigate to — so this checks it FIRST
    // and refuses to navigate at all, rather than sending the reviewer into a
    // composer that cannot submit. The toast points back at the assign/create
    // control (the breadcrumb chip / approve gate) instead of duplicating it
    // here — a toast cannot host that control's own search-and-create state.
    if (supplierMatched === false) {
      toast.warning(translate("MC_DECL_V2_SUP_REQUEST_NEEDS_SUPPLIER"))
      return
    }
    // Prefill the composer with what's already known here: the matched
    // supplier and whatever's still open on the framework the reviewer was
    // looking at. Both are optional on the composer side — an unmatched
    // supplier never reaches this line (guarded above), and an empty
    // `openArticleIds` (e.g. Anfordern fired with no framework active) just
    // omits the param, same as the old bare navigate.
    let articleIds = openArticleIds
    if (resolveOpenArticleIds) {
      try {
        articleIds = await resolveOpenArticleIds()
      } catch {
        // The pool did not load: open the composer without an article scope rather
        // than not at all (the composer's own default), the same as an empty list.
        articleIds = NO_ARTICLE_IDS
      }
    }
    const query = buildRequestSearchParams({
      supplierId,
      articleIds,
      context: requestContext,
    }).toString()
    navigate(
      `/material-compliance/supplier-studio/inquiry/new${query ? `?${query}` : ""}`,
    )
  }

  const handleUeberspringen = () => {
    toast.success(translate("MC_DECL_V2_FOOTER_DEFER_TOAST"))
    goToNextOrList()
  }

  const focusApprove = () => {
    // Compact, active Verordnung not yet geprueft: the accept button above
    // is not rendered at all (`order`'s own filter on `compactConformNext`),
    // so landing on it would find nothing and fall through to the "all
    // checked" toast below, false in exactly this state (fix wave,
    // TUR-1211). The conform-next button is compact's own stand-in primary,
    // so Weiter's terminal lands there instead.
    const el = compactConformNext
      ? conformNextButtonRef.current
      : acceptButtonRef.current
    if (!el) {
      // A user without approve permission has no accept button to land on
      // (`order` filters it out) — say what the terminal means instead of
      // making Weiter a silent dead key (claude-review, PR #2066).
      toast.success(translate("MC_DECL_V2_FOOTER_ALL_CHECKED_TOAST"))
      return
    }
    el.focus()
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
    // Remove + reflow + re-add so a Weiter press MID-flash restarts the
    // animation instead of silently keeping the running one.
    el.classList.remove("sv2-flash")
    void el.offsetWidth
    el.classList.add("sv2-flash")
    flashTimerRef.current = setTimeout(
      () => el.classList.remove("sv2-flash"),
      1200,
    )
  }

  useImperativeHandle(
    ref,
    () => ({
      approve: () => void handleApprove(),
      // Same as the click: the collector modal, never the composer directly
      // (the questionnaire request lives inside the modal, W5-1).
      request: openRequestDialog,
      // Compact hides the whole-declaration Skip button ("3" fires this via
      // `useStudioV2Keyboard`'s `onDefer`, fix wave TUR-1211): the imperative
      // twin must not run the hidden button's own action either.
      defer: () => {
        if (!compact) handleUeberspringen()
      },
      focusApprove,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      approveDisabled,
      approveDisabledReason,
      compact,
      compactConformNext,
      declId,
      nextDeclarationId,
      onNoNextDeclaration,
      precheckOn,
      startFinalReview,
      supplierMatched,
      supplierId,
      openArticleIds,
      loadAllCards,
      requestLoading,
    ],
  )

  const approveLabel =
    frameworks.length > 1
      ? translate("MC_DECL_V2_FOOTER_APPROVE_ALL")
      : translate("MC_DECL_V2_FOOTER_APPROVE")

  const actClass = (action: PrimaryAction) =>
    cn("sv2-act", action === primaryAction && "sv2-primary")

  // TUR-1563: Approve keeps the base `.sv2-act` shape while blocked, but
  // never the primary (yellow) look: that look promises "press this now",
  // which a blocked button cannot honour. `derivePrimaryAction` is
  // unchanged: `primaryAction` can still read "accept" while blocked, this
  // only withholds the CLASS, never which action the caller considers
  // primary.
  const acceptButtonClass = cn(
    "sv2-act",
    primaryAction === "accept" && !approveBlocked && "sv2-primary",
  )

  const buttonOf = (action: PrimaryAction) => {
    switch (action) {
      case "accept": {
        const button = (
          <button
            type="button"
            ref={acceptButtonRef}
            // TUR-1835: what the chat assistant pulses for "finish the review".
            data-agent-target="declaration.finish-review"
            className={acceptButtonClass}
            aria-disabled={approveDisabled || approve.isPending || undefined}
            onClick={() => void handleApprove()}
          >
            {/* Spins through the REVIEW_PENDING wait too, not just the
                request itself: from here on the press IS pending — it is
                simply waiting on the final review before it can land. In
                place of the label, so the button keeps its size (TUR-1680). */}
            <BusyLabel
              busy={approve.isPending || awaitingReview || pressQueued}
            >
              {approveLabel}
            </BusyLabel>
          </button>
        )
        if (!approveDisabled || !approveDisabledReason) {
          return <span key="accept">{button}</span>
        }
        return (
          <TooltipProvider key="accept" delayDuration={200}>
            <Tooltip>
              {/* `aria-disabled`, never native `disabled`: a native `disabled`
                  button drops out of the tab order, so a keyboard user could
                  never reach it (or the reason the tooltip below states) —
                  the wrapping span that used to work around that is gone now
                  that the TRIGGER is the button itself, which is also what
                  lets Radix wire the open tooltip's `aria-describedby` onto
                  it automatically. `handleApprove` is the SAME guard the 1 /
                  Enter keyboard path already used — a click while blocked
                  now runs it too, instead of the native attribute silently
                  eating the event. */}
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="top">
                {/* TUR-1563: while blocked by open work (never the supplier/
                    inquiry/no-frameworks reasons, which already name their
                    own blocker), a second line names the FIRST open point:
                    same row the open-points popover would show first. */}
                <div>{approveDisabledReason}</div>
                {firstOpenPoint && <div>{firstOpenPoint}</div>}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }
      case "request": {
        // TUR-1211 (W5-1 fix): Anfordern itself carries the collected-
        // questions counter and opens the request modal. The questionnaire
        // navigation (`handleAnfordern`) moved INSIDE the modal as its own
        // secondary action (see the dialog mount below). Opening the modal
        // needs no supplier gate: sending needs a recipient, and the
        // composer shows its own inline hint for that; the questionnaire
        // action alone still runs the pre-existing supplier check.
        const button = (
          <button
            key="request"
            type="button"
            data-testid="footer-request"
            // Primary styling borrows from the teammate's package button
            // (Ferdi's decision): OUR collector/dialog stay the send path,
            // his count/label only lend the "something is waiting" look.
            // One primary at a time (review finding F10, wave 5): the borrow
            // is withheld only while Approve ACTUALLY SHOWS as primary
            // (TUR-1563): `primaryAction === "accept"` alone is no longer
            // enough, since a blocked Approve no longer carries the look
            // itself (`acceptButtonClass` above). With drafts waiting and
            // Approve blocked, Anfordern still gets the borrowed look; once
            // Approve is unblocked, unchanged (withheld).
            className={cn(
              actClass("request"),
              draftCount > 0 &&
                !(primaryAction === "accept" && !approveBlocked) &&
                "sv2-primary",
            )}
            onClick={openRequestDialog}
            // TUR-1902: without follow-up questions the press opens the
            // questionnaire request (`openRequestDialog`); say so.
            title={
              inquiryFlag
                ? undefined
                : translate("MC_DECL_V2_FOOTER_REQUEST_QUESTIONNAIRE_TITLE")
            }
            disabled={requestLoading || undefined}
            aria-busy={requestLoading || undefined}
          >
            {translate("MC_DECL_V2_FOOTER_REQUEST")}
            {draftCount > 0 && (
              <span
                data-testid="footer-questions-count"
                className="sv2-tpill ml-1.5"
              >
                {draftCount}
              </span>
            )}
          </button>
        )
        if (!requestTooltip) return <span key="request">{button}</span>
        return (
          <TooltipProvider key="request" delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent side="top">
                {translate("MC_DECL_V2_FOOTER_REQUEST_TOOLTIP", {
                  list: requestTooltip,
                })}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }
      case "defer":
        return (
          <button
            key="defer"
            type="button"
            className={actClass("defer")}
            onClick={handleUeberspringen}
          >
            {translate("MC_DECL_V2_FOOTER_DEFER")}
          </button>
        )
    }
  }

  // `compactConformNext` is declared above (near the refs) so `focusApprove`
  // can read it. Compact's own primary before the active Verordnung is
  // geprueft: a separate element below (never `buttonOf("accept")`), so this
  // drops the built-in Approve from `order` in exactly that state, the same
  // "not rendered" the brief calls for, without a third branch inside
  // `buttonOf`.
  const order = [
    primaryAction,
    ...ACTION_ORDER.filter((a) => a !== primaryAction),
  ]
    .filter((a) => a !== "accept" || (canApprove && !compactConformNext))
    // "Zurückstellen" (whole-declaration Skip) never renders in compact:
    // its own per-Verordnung Überspringen (below) replaces it there.
    .filter((a) => a !== "defer" || !compact)

  return (
    <div className="shrink-0">
      {gateDetail && (
        <ApproveGatePanel
          detail={gateDetail}
          nameOf={nameOf}
          declId={declId}
          supplierMentioned={supplierMentioned}
          // The refusal is answered once the re-extraction starts: the gate
          // clears rather than staying up over the re-read declaration.
          onReextract={
            onReextract
              ? () => {
                  setGateDetail(null)
                  onReextract()
                }
              : undefined
          }
          onGoToStatement={onGoToStatement}
          autoRetrying={awaitingReview}
          recomputeFinished={recomputeFinished}
          localBlocker={
            waitBlockedLocally && approveDisabledReason
              ? { reason: approveDisabledReason, firstOpenPoint }
              : null
          }
          approving={approving}
          labelOf={labelOf}
          verdictOf={verdictOf}
        />
      )}
      <div className="sv2-revfoot">
        {/* Left slot for the declaration-wide bulk gesture ("alle konformen
            als geprüft markieren") — promoted here from the navigator's own
            footer, where it rendered as an ellipsized micro-label in a 12%
            pane while the action bar's left half sat empty (product-owner
            call, 25.08.). The gate text stays glued to the actions on the
            right: it explains THEIR state, not the bulk gesture's. */}
        <div className="sv2-footleft">{bulkAction}</div>
        {/* Beside the gate text, never instead of it: a recalculation can take
            minutes, and the open-points map (D1) must stay usable meanwhile. */}
        {recomputeShown && (
          <span className="sv2-gate" data-testid="footer-recompute-status">
            <RecomputeStatusLine declId={declId} status={recompute} />
          </span>
        )}
        <span className="sv2-gate" data-testid="footer-gate-text">
          {approveInProgress ? (
            translate(
              approving
                ? "MC_DECL_V2_FOOTER_APPROVING"
                : "MC_DECL_V2_FOOTER_APPROVE_CHECKING",
            )
          ) : saving ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              {translate("MC_DECL_V2_FOOTER_SAVING")}
            </span>
          ) : openItemsStatus === "unknown" ? (
            translate("MC_DECL_V2_FOOTER_OPEN_ITEMS_UNKNOWN")
          ) : openItemsStatus === "pending" ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              {translate("MC_DECL_V2_FOOTER_OPEN_ITEMS_PENDING")}
            </span>
          ) : noFrameworks ? (
            translate("MC_DECL_V2_NO_FRAMEWORKS")
          ) : openPoints > 0 ? (
            // D1 (TUR-1449): the clickable map replaces the bare
            // "Noch x von n Verordnungen" line — same slot, same quiet role,
            // now naming every blocker with a jump BEFORE the reviewer runs
            // into the post-click gate.
            <OpenPointsPopover
              declId={declId}
              worklist={worklist}
              nameOf={nameOf}
              supplierMatched={supplierMatched}
              supplierMentioned={supplierMentioned}
              sentFrameworks={sentFrameworks}
              onGoToStatement={onGoToStatement}
              onGoToFramework={onGoToFramework}
              labelOf={labelOf}
              cardOrderOf={cardOrderOf}
              verdictOf={verdictOf}
              reasonOf={reasonOf}
              frameworkOrder={frameworks}
              onOpen={
                loadAllCards
                  ? () => void loadAllCards({ openOnly: true }).catch(() => {})
                  : undefined
              }
            />
          ) : heldArticleCount === 1 ? (
            translate("MC_DECL_V2_FOOTER_INQUIRY_HOLDBACK_ONE")
          ) : heldArticleCount > 1 ? (
            translate("MC_DECL_V2_FOOTER_INQUIRY_HOLDBACK", {
              count: String(heldArticleCount),
            })
          ) : (
            translate("MC_DECL_V2_FOOTER_ALL_CHECKED")
          )}
        </span>
        {/* TUR-1935: a remembered press, announced (the spinner alone is silent
            to a screen reader). Always mounted, so the live region exists
            before its text changes. */}
        <span
          role="status"
          aria-live="polite"
          className="sr-only"
          data-testid="approve-press-queued"
        >
          {pressQueued ? translate("MC_DECL_V2_APPROVE_PRESS_QUEUED") : ""}
        </span>
        <div className="sv2-acts">
          {/* Navigation before decisions: the guided layer's Weiter sits left
              of the three actions, present whenever StudioV2Layout passes
              the handler (always, now that the layer is standard — TUR-1211).
              NOT a fourth direct `.sv2-revfoot` child — that would break the
              gate text's margin-left:auto grouping. */}
          {onNextOpenPoint && !geprueft && (
            <button
              type="button"
              className="sv2-act sv2-next"
              data-testid="next-open-point"
              title={translate("MC_DECL_V2_NEXT_OPEN_TITLE")}
              onClick={onNextOpenPoint}
            >
              {translate("MC_DECL_V2_NEXT_OPEN_LABEL")}
            </button>
          )}
          {/* Compact's own primary (design spec "Flow"): the per-Verordnung
              bundle-conform-then-advance gesture, standing in for Approve
              while the active Verordnung itself is not yet geprueft (Approve
              is dropped from `order` above for exactly this state). */}
          {compactConformNext && (
            <button
              type="button"
              ref={conformNextButtonRef}
              data-testid="footer-conform-next"
              className="sv2-act sv2-primary"
              onClick={focusFooter?.onConformActive}
            >
              {translate("MC_DECL_V2_FOCUS_CONFORM_NEXT")}
            </button>
          )}
          {order.map(buttonOf)}
          {/* Compact's own secondary: skip the active Verordnung WITHOUT
              conforming it. Stays available for as long as any Verordnung is
              still open, even once THIS one already is (advancing is still a
              real move; Approve above may still be gated on the others). */}
          {focusFooter && !geprueft && (
            <button
              type="button"
              data-testid="footer-skip-framework"
              className="sv2-act"
              onClick={focusFooter.onSkipFramework}
            >
              {translate("MC_DECL_V2_FOCUS_SKIP")}
            </button>
          )}
        </div>
      </div>
      <RequestQuestionsDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        declId={declId}
        supplierId={supplierId}
        nameOf={nameOf}
        onQuestionnaireRequest={handleAnfordern}
        cards={cards}
      />
      <OpenQuestionsAckDialog
        open={ackDialogBatches !== null}
        onOpenChange={(next) => {
          if (!next) setAckDialogBatches(null)
        }}
        declId={declId}
        batches={ackDialogBatches ?? []}
        onConfirm={(reason) => void handleApprove({ ack: reason })}
        pending={approve.isPending}
      />
      <ApproveConfirmDialog
        open={confirmOpen}
        declId={declId}
        onCancel={handleConfirmCancel}
        onConfirm={() =>
          void handleApprove({
            ack: ackReasonRef.current ?? undefined,
            confirmed: true,
          })
        }
        sending={approve.isPending}
        saving={saving}
        checking={approveChecking}
        describeStatement={(fw, statementId) =>
          labelOf
            ? `${nameOf(fw)} · ${translate("MC_DECL_V2_UNIT_LABEL", {
                i: labelOf(fw, statementId, undefined),
              })}`
            : nameOf(fw)
        }
      />
    </div>
  )
})
